import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    BUSINESS_REFERENCE_OPTIONS,
    OBLIGATION_TYPE_OPTIONS,
    type Claim,
    type CreateClaimItemPayload,
    type CreateClaimPayload,
    type LegalCaseFormOptions,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Gerencia a pretensão (Claim) de um caso: cria, edita os valores e adiciona/remove itens. O
// estado inicial vem do page-view (`initialClaim`), re-semeado quando o caso muda; após cada
// mutação, `fetchClaim` recarrega só o claim (sem recarregar o page-view inteiro).
// A regra anti-duplicação (INSTALLMENT + GUARANTEE da mesma operação) é do backend — aqui o
// 422 vira errorFields/toast; a garantia fica fora do total (o backend já projeta assim).
export default function useLegalCaseClaim(caseId: string | null, initialClaim?: Claim | null) {
    const [claim, setClaim] = useState<Claim | null>(initialClaim || null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [businessReferenceOptions, setBusinessReferenceOptions] =
        useState<SelectOption[]>(BUSINESS_REFERENCE_OPTIONS);
    const [obligationTypeOptions, setObligationTypeOptions] =
        useState<SelectOption[]>(OBLIGATION_TYPE_OPTIONS);
    // Caso já semeado. O `initialClaim` vem do page-view (o detalhe passa `detail.claim`), então
    // o estado é semeado a partir dele — sem rede extra. Mas o `useState(initialClaim)` só vale
    // na montagem; se o `caseId` mudar sem remontar o componente (navegar de um caso para outro),
    // o estado ficaria preso no claim antigo. Re-seeda quando o caso muda de fato. Mesmo padrão
    // do `useLegalCaseParties` (Task 3), que resolveu isso com `seededCaseRef`.
    const seededCaseRef = useRef<string | null>(null);
    // Geração de request: guarda contra resposta obsoleta. O fetch do mount pode chegar depois
    // de uma mutação e sobrescrever o claim com o snapshot antigo. Cada fetch captura a geração
    // ao iniciar e só aplica o resultado se ainda for a corrente (o próprio ++ invalida os em voo).
    const fetchGenRef = useRef(0);

    const fetchClaim = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getClaim(caseId);
            if (fetchGenRef.current === generation) setClaim(result.claim || null);
        } catch {
            // silencioso — a seção mantém o que veio no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setClaim(initialClaim || null);
        setErrorFields([]);
        // Semeia com o page-view (paint imediato) e re-busca do servidor. O re-fetch cobre a
        // remontagem da seção (ao alternar de aba): sem ele, voltaria ao `initialClaim` do
        // page-view — carregado antes de qualquer edição — e a alteração recém-feita "sumiria"
        // até recarregar a página. initialClaim fora das deps de propósito: o seed é por caso;
        // create/update/add/remove atualizam via fetchClaim.
        fetchClaim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        let active = true;
        fetchFormOptions().then((opts) => {
            if (!active || !opts) return;
            if (opts.businessReferences?.length) setBusinessReferenceOptions(opts.businessReferences);
            if (opts.obligationTypes?.length) setObligationTypeOptions(opts.obligationTypes);
        });
        return () => { active = false; };
    }, []);

    const clearErrors = () => setErrorFields([]);

    const createClaim = async (data: CreateClaimPayload): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.createClaim(caseId, data);
            await fetchClaim();
            toast.success('Pretensão criada');
            return true;
        } catch (e: any) {
            return handleError(e, 'Erro ao criar pretensão', setErrorFields);
        } finally {
            setIsSaving(false);
        }
    };

    const updateClaim = async (data: CreateClaimPayload): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateClaim(caseId, data);
            await fetchClaim();
            toast.success('Pretensão atualizada');
            return true;
        } catch (e: any) {
            return handleError(e, 'Erro ao atualizar pretensão', setErrorFields);
        } finally {
            setIsSaving(false);
        }
    };

    const addItem = async (data: CreateClaimItemPayload): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addClaimItem(caseId, data);
            await fetchClaim();
            toast.success('Título adicionado');
            return true;
        } catch (e: any) {
            return handleError(e, 'Erro ao adicionar título', setErrorFields);
        } finally {
            setIsSaving(false);
        }
    };

    const removeItem = async (itemId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeClaimItem(caseId, itemId);
            await fetchClaim();
            toast.success('Título removido');
        } catch (e: unknown) {
            // Mesmo tratamento das demais operações: prioriza e.data.message (mensagem útil
            // do backend, ex.: 404) sobre a genérica do ApiError.
            handleError(e, 'Erro ao remover título', setErrorFields);
        } finally {
            setIsSaving(false);
        }
    };

    return {
        claim, isLoading, isSaving, errorFields,
        businessReferenceOptions, obligationTypeOptions,
        createClaim, updateClaim, addItem, removeItem, clearErrors,
    };
}

// Extraída para fora do hook (evita função dentro de função — diretriz de qualidade do repo):
// busca as opções do page-form; devolve null em falha (o fallback local já preenche os selects).
async function fetchFormOptions(): Promise<LegalCaseFormOptions | null> {
    try {
        return await recoveryApi.getLegalCaseFormOptions();
    } catch {
        return null;
    }
}

// Extraída para fora do hook (evita função dentro de função): traduz o erro da API em
// toast + errorFields e devolve false para o caller.
function handleError(
    e: unknown,
    fallbackMessage: string,
    setErrorFields: (fields: ErrorField[]) => void,
): boolean {
    if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
        toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
        setErrorFields(e.data.errors);
        return false;
    }
    if (e instanceof ApiError && (e.status === 404 || e.status === 409)) {
        toast.error(e.data?.message || fallbackMessage);
        return false;
    }
    const message = e instanceof Error ? e.message : fallbackMessage;
    toast.error(message || fallbackMessage);
    return false;
}
